import { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Package, Upload, Filter, ChevronDown, Info, Loader2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { BadgeStaleness } from "@/components/ui/badge-staleness";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTenant } from "@/hooks/useTenant";
import { FamilySwitcher } from "@/components/FamilySwitcher";
import { AltInspectorDrawer } from "@/components/portfolio/AltInspectorDrawer";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

// Helper to classify alt vehicle type
const classifyAltAssetClass = (type: string | null, name: string | null): string => {
  const t = (type || '').toLowerCase();
  const n = (name || '').toLowerCase();

  if (t.includes('pe') || t.includes('buyout') || n.includes('private equity')) return 'Private Equity';
  if (t.includes('vc') || t.includes('venture') || n.includes('venture')) return 'Venture Capital';
  if (t.includes('real estate') || t.includes('reit') || n.includes('real estate') || n.includes('property')) return 'Real Estate';
  if (t.includes('infra') || n.includes('infrastructure')) return 'Infrastructure';
  if (t.includes('credit') || t.includes('debt') || t.includes('lending') || n.includes('credit')) return 'Credit';
  if (t.includes('hedge') || n.includes('hedge')) return 'Hedge Fund';
  return 'Other';
};

const getStatusBadge = (status: string) => {
  const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
    computed: "secondary",
    stale: "outline",
    missing: "destructive",
    override_proposed: "default",
    approved: "secondary",
    locked: "outline",
  };
  
  const labels: Record<string, string> = {
    computed: "Computed",
    stale: "Stale",
    missing: "Missing",
    override_proposed: "Override Proposed",
    approved: "Approved",
    locked: "Locked",
  };

  return <Badge variant={variants[status] || "default"}>{labels[status] || status}</Badge>;
};

const getStalenessColor = (days: number) => {
  if (days === 0) return "text-muted-foreground";
  if (days > 90) return "text-destructive";
  if (days > 60) return "text-orange-500";
  if (days > 30) return "text-yellow-600";
  return "text-muted-foreground";
};

export default function Alternatives() {
  const { orgId, isMFO, familyId } = useTenant();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [selectedYear, setSelectedYear] = useState(String(new Date().getFullYear()));
  const [periodKind, setPeriodKind] = useState<"quarter" | "month">("quarter");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedAssetClass, setSelectedAssetClass] = useState<string>("all");
  const [selectedCell, setSelectedCell] = useState<{ altId: string; quarter: string; assetClass: string; vehicleName: string } | null>(null);

  // Pre-select asset class from query params if provided
  useEffect(() => {
    const assetClassParam = searchParams.get("assetClass");
    if (assetClassParam) {
      setSelectedAssetClass(assetClassParam);
    }
  }, [searchParams]);

  // Close drawer when family changes (MFO)
  useEffect(() => {
    setSelectedCell(null);
  }, [familyId]);

  // Fetch alt vehicles with their period metrics
  const { data: altVehiclesData, isLoading } = useQuery({
    queryKey: ['alt-vehicles', orgId, familyId, selectedYear],
    queryFn: async () => {
      if (!orgId) return [];

      // Fetch alt vehicles
      let vehiclesQuery = supabase
        .from('alt_vehicles')
        .select('*')
        .eq('org_id', orgId);

      if (familyId) {
        vehiclesQuery = vehiclesQuery.eq('family_id', familyId);
      }

      const { data: vehicles, error: vehiclesError } = await vehiclesQuery;
      if (vehiclesError) throw vehiclesError;

      if (!vehicles || vehicles.length === 0) return [];

      // Fetch period metrics for these vehicles for the selected year
      const vehicleIds = vehicles.map(v => v.id);
      const yearStart = `${selectedYear}-01-01`;
      const yearEnd = `${selectedYear}-12-31`;

      const { data: metrics, error: metricsError } = await supabase
        .from('alt_period_metrics')
        .select('*')
        .in('alt_id', vehicleIds)
        .gte('period_end', yearStart)
        .lte('period_end', yearEnd)
        .order('period_end', { ascending: true });

      if (metricsError) throw metricsError;

      // Group metrics by vehicle
      const metricsByVehicle: Record<string, Array<{ alt_id: string; period_end: string; called_cum?: number; distributed_cum?: number; called_period?: number; distributed_period?: number; computed_nav?: number; tvpi?: number; dpi?: number; moic?: number; status?: string }>> = {};
      metrics?.forEach(m => {
        if (!metricsByVehicle[m.alt_id]) {
          metricsByVehicle[m.alt_id] = [];
        }
        metricsByVehicle[m.alt_id].push(m);
      });

      // Transform to display format
      const now = new Date();
      return vehicles.map(v => {
        const vehicleMetrics = metricsByVehicle[v.id] || [];
        const assetClass = classifyAltAssetClass(v.status, v.vehicle_name);
        const vintage = v.vintage || new Date(v.created_at).getFullYear();

        // Create periods for each quarter
        const periods = ['Q1', 'Q2', 'Q3', 'Q4'].map((q, idx) => {
          const quarterEnd = new Date(Number(selectedYear), (idx + 1) * 3, 0); // Last day of quarter
          const metric = vehicleMetrics.find(m => {
            const periodDate = new Date(m.period_end);
            return periodDate.getMonth() >= idx * 3 && periodDate.getMonth() < (idx + 1) * 3;
          });

          if (!metric) {
            return {
              quarter: `${q}'${selectedYear.slice(-2)}`,
              nav: 0,
              calledPeriod: 0,
              calledCum: 0,
              distributedPeriod: 0,
              distributedCum: 0,
              tvpi: 0,
              dpi: 0,
              moic: 0,
              asOf: '',
              staleDays: 0,
              status: 'missing' as const
            };
          }

          const periodDate = new Date(metric.period_end);
          const staleDays = Math.max(0, Math.floor((now.getTime() - periodDate.getTime()) / (1000 * 60 * 60 * 24)));

          // Determine status based on staleness and other factors
          let status: 'stale' | 'computed' | 'approved' | 'locked' | 'missing' | 'override_proposed' = 'computed';
          if (staleDays > 90) status = 'stale';
          else if (staleDays === 0 || metric.status === 'locked') status = 'locked';
          else if (metric.status === 'approved') status = 'approved';
          else if (metric.status === 'override_proposed') status = 'override_proposed';

          // Use the correct column names from alt_period_metrics
          const called = Number(metric.called_cum) || 0;
          const distributed = Number(metric.distributed_cum) || 0;
          const nav = Number(metric.computed_nav) || 0;
          // Use stored tvpi/dpi/moic if available, otherwise calculate
          const tvpi = metric.tvpi ?? (called > 0 ? (nav + distributed) / called : 0);
          const dpi = metric.dpi ?? (called > 0 ? distributed / called : 0);
          const moic = metric.moic ?? (called > 0 ? nav / called : 0);

          return {
            quarter: `${q}'${selectedYear.slice(-2)}`,
            nav,
            calledPeriod: Number(metric.called_period) || 0,
            calledCum: called,
            distributedPeriod: Number(metric.distributed_period) || 0,
            distributedCum: distributed,
            tvpi,
            dpi,
            moic,
            asOf: metric.period_end,
            staleDays,
            status
          };
        });

        return {
          id: v.id,
          manager: v.manager_name || 'Unknown Manager',
          vehicle: v.vehicle_name || 'Unknown Fund',
          assetClass,
          vintage,
          currency: v.currency || 'USD',
          status: v.status || 'Active',
          commitment: Number(v.commitment) || 0,
          periods
        };
      });
    },
    enabled: !!orgId
  });

  const years = ["2021", "2022", "2023", "2024", "2025", "2026"];
  const quarters = ["Q1", "Q2", "Q3", "Q4"];
  const assetClasses = ["all", "Private Equity", "Venture Capital", "Real Estate", "Infrastructure", "Credit", "Hedge Fund", "Other"];

  const filteredAlts = useMemo(() => {
    if (!altVehiclesData) return [];
    return altVehiclesData.filter((alt) => {
      const matchesSearch = alt.vehicle.toLowerCase().includes(searchQuery.toLowerCase()) ||
        alt.manager.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesAssetClass = selectedAssetClass === "all" || alt.assetClass === selectedAssetClass;
      return matchesSearch && matchesAssetClass;
    });
  }, [altVehiclesData, searchQuery, selectedAssetClass]);

  return (
    <div className="flex-1 space-y-4 p-4 md:p-8 pt-6">
      <Alert className="border-blue-200 bg-blue-50/50">
        <Info className="h-4 w-4" />
        <AlertTitle>New Unified Holdings View</AlertTitle>
        <AlertDescription className="flex items-center justify-between">
          <span>
            The Quarterly Grid is now integrated into the main Holdings page with an enhanced detail drawer for deep-dive analysis.
          </span>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate('/portfolio/positions')}
            className="ml-4"
          >
            Go to Holdings
          </Button>
        </AlertDescription>
      </Alert>

      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-4">
            <h2 className="text-2xl font-bold tracking-tight">Alternatives — Quarterly Grid</h2>
            {isMFO && <FamilySwitcher />}
          </div>
          <p className="text-muted-foreground">
            Period-by-period marks, overrides, and performance metrics for alternative investments
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm">
            <Upload className="h-4 w-4" />
            Import
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <CardTitle>Quarterly Grid</CardTitle>
              <CardDescription>
                Excel-style grid showing NAV, cash flows, and performance across periods
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <Input
                placeholder="Search by manager or vehicle..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="max-w-sm"
              />
            </div>
            <Select value={selectedAssetClass} onValueChange={setSelectedAssetClass}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Asset Class" />
              </SelectTrigger>
              <SelectContent>
                {assetClasses.map((ac) => (
                  <SelectItem key={ac} value={ac}>
                    {ac === "all" ? "All Asset Classes" : ac}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Tabs value={periodKind} onValueChange={(v) => setPeriodKind(v as "quarter" | "month")}>
              <TabsList>
                <TabsTrigger value="quarter">Quarterly</TabsTrigger>
                <TabsTrigger value="month">Monthly</TabsTrigger>
              </TabsList>
            </Tabs>
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map((year) => (
                  <SelectItem key={year} value={year}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <div className="text-center py-12">
              <Loader2 className="h-12 w-12 mx-auto text-muted-foreground mb-4 animate-spin" />
              <p className="text-muted-foreground">Loading alternatives...</p>
            </div>
          ) : filteredAlts.length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No alternatives found. Upload fund statements to populate.</p>
            </div>
          ) : (
          <div className="border rounded-lg overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="sticky left-0 bg-background z-10 min-w-[200px]">
                    Manager ▸ Vehicle
                  </TableHead>
                  <TableHead className="sticky left-[200px] bg-background z-10">Asset Class</TableHead>
                  <TableHead className="sticky left-[320px] bg-background z-10">Vintage</TableHead>
                  <TableHead className="sticky left-[400px] bg-background z-10">Status</TableHead>
                  <TableHead className="sticky left-[480px] bg-background z-10">Commitment</TableHead>
                  {quarters.map((q) => (
                    <TableHead key={q} className="min-w-[180px] text-center">
                      {q}'{selectedYear.slice(-2)}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAlts.map((alt) => (
                  <TableRow key={alt.id}>
                    <TableCell className="sticky left-0 bg-background z-10 font-medium">
                      <div className="space-y-1">
                        <div className="font-semibold">{alt.manager}</div>
                        <div className="text-sm text-muted-foreground">{alt.vehicle}</div>
                      </div>
                    </TableCell>
                    <TableCell className="sticky left-[200px] bg-background z-10">
                      <Badge variant="outline">{alt.assetClass}</Badge>
                    </TableCell>
                    <TableCell className="sticky left-[320px] bg-background z-10">
                      <Badge variant="outline">{alt.vintage}</Badge>
                    </TableCell>
                    <TableCell className="sticky left-[400px] bg-background z-10">
                      <Badge variant={alt.status === "Active" ? "default" : "secondary"}>
                        {alt.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="sticky left-[480px] bg-background z-10">
                      ${(alt.commitment / 1000000).toFixed(1)}M
                    </TableCell>
                    {alt.periods.map((period, idx) => (
                      <TableCell
                        key={idx}
                        className="cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => setSelectedCell({ altId: alt.id, quarter: period.quarter, assetClass: alt.assetClass, vehicleName: alt.vehicle })}
                      >
                        {period.status === "missing" ? (
                          <div className="text-center py-2">
                            <Badge variant="destructive">Missing</Badge>
                          </div>
                        ) : (
                          <div className="min-w-[200px]">
                            <div className="border rounded">
                              <table className="w-full text-xs">
                                <tbody>
                                  <tr className="border-b bg-muted/30">
                                    <td className="px-2 py-1 font-medium">NAV</td>
                                    <td className="px-2 py-1 text-right font-semibold">
                                      ${(period.nav / 1000000).toFixed(2)}M
                                    </td>
                                  </tr>
                                  <tr className="border-b">
                                    <td className="px-2 py-1 text-muted-foreground">Called (P)</td>
                                    <td className="px-2 py-1 text-right">${(period.calledPeriod / 1000000).toFixed(1)}M</td>
                                  </tr>
                                  <tr className="border-b">
                                    <td className="px-2 py-1 text-muted-foreground">Called (Σ)</td>
                                    <td className="px-2 py-1 text-right">${(period.calledCum / 1000000).toFixed(1)}M</td>
                                  </tr>
                                  <tr className="border-b">
                                    <td className="px-2 py-1 text-muted-foreground">Dist (P)</td>
                                    <td className="px-2 py-1 text-right">${(period.distributedPeriod / 1000000).toFixed(1)}M</td>
                                  </tr>
                                  <tr className="border-b">
                                    <td className="px-2 py-1 text-muted-foreground">Dist (Σ)</td>
                                    <td className="px-2 py-1 text-right">${(period.distributedCum / 1000000).toFixed(1)}M</td>
                                  </tr>
                                  <tr className="border-b bg-muted/30">
                                    <td className="px-2 py-1 font-medium">TVPI</td>
                                    <td className="px-2 py-1 text-right font-medium">{period.tvpi.toFixed(2)}x</td>
                                  </tr>
                                  <tr className="border-b">
                                    <td className="px-2 py-1 font-medium">DPI</td>
                                    <td className="px-2 py-1 text-right">{period.dpi.toFixed(2)}x</td>
                                  </tr>
                                  <tr className="border-b">
                                    <td className="px-2 py-1 font-medium">MOIC</td>
                                    <td className="px-2 py-1 text-right">{period.moic.toFixed(2)}x</td>
                                  </tr>
                                  <tr>
                                    <td className="px-2 py-1" colSpan={2}>
                                      <div className="flex items-center justify-between gap-2">
                                        <BadgeStaleness days={period.staleDays} />
                                        {getStatusBadge(period.status)}
                                      </div>
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          )}
        </CardContent>
      </Card>

      <AltInspectorDrawer
        open={selectedCell !== null}
        onClose={() => setSelectedCell(null)}
        altId={selectedCell?.altId || ""}
        quarter={selectedCell?.quarter || ""}
        assetClass={selectedCell?.assetClass}
        vehicleName={selectedCell?.vehicleName}
      />
    </div>
  );
}
